import { useState, useEffect, useRef } from 'react';
import { client } from '../../api/client';

interface NewsletterSubscriber {
  id: number;
  email: string;
  name: string | null;
  user_id: number | null;
  status: 'active' | 'unsubscribed' | 'bounced';
  source: 'registration' | 'homepage_form' | 'admin' | 'import' | null;
  subscribed_at: string;
  unsubscribed_at: string | null;
  group_ids: number[];
}

interface NewsletterGroup {
  id: number;
  name: string;
  description: string | null;
  color: string;
  subscriber_count: number;
  created_at: string;
}

type ActivePanel = 'subscribers' | 'groups' | 'import';

export function NewsletterTab() {
  // Data
  const [subscribers, setSubscribers] = useState<NewsletterSubscriber[]>([]);
  const [groups, setGroups] = useState<NewsletterGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'unsubscribed'>('all');
  const [groupFilter, setGroupFilter] = useState<number | 'all'>('all');

  // Selection
  const [selectedSubscribers, setSelectedSubscribers] = useState<number[]>([]);

  // Active panel
  const [activePanel, setActivePanel] = useState<ActivePanel>('subscribers');

  // Modals
  const [showSendModal, setShowSendModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showAssignGroupModal, setShowAssignGroupModal] = useState<number | null>(null);

  // Forms
  const [sendForm, setSendForm] = useState({ subject: '', message: '' });
  const [sendTargetMode, setSendTargetMode] = useState<'all' | 'selected' | 'groups'>('all');
  const [sendTargetGroupIds, setSendTargetGroupIds] = useState<number[]>([]);
  const [sending, setSending] = useState(false);

  const [groupForm, setGroupForm] = useState({ name: '', description: '', color: '#f97316' });
  const [editingGroup, setEditingGroup] = useState<NewsletterGroup | null>(null);

  const [importText, setImportText] = useState('');
  const [importGroupIds, setImportGroupIds] = useState<number[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [subsRes, groupsRes] = await Promise.all([
        client.api.fetch('/admin/newsletter/subscribers'),
        client.api.fetch('/admin/newsletter/groups'),
      ]);
      const subsData = await subsRes.json();
      const groupsData = await groupsRes.json();
      if (subsData.success) setSubscribers(subsData.data || []);
      if (groupsData.success) setGroups(groupsData.data || []);
    } catch {
      setError('Greška pri povezivanju sa serverom');
    } finally {
      setLoading(false);
    }
  };

  // ---- Subscriber actions ----

  const handleDeleteSubscriber = async (id: number) => {
    if (!window.confirm('Obrisati ovog pretplatnika?')) return;
    try {
      const res = await client.api.fetch(`/admin/newsletter/subscribers/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setSubscribers(prev => prev.filter(s => s.id !== id));
        setSelectedSubscribers(prev => prev.filter(sid => sid !== id));
      } else alert(data.error || 'Greška');
    } catch { alert('Greška pri povezivanju'); }
  };

  const handleToggleStatus = async (id: number, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'unsubscribed' : 'active';
    try {
      const res = await client.api.fetch(`/admin/newsletter/subscribers/${id}/status`, {
        method: 'PUT', body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json();
      if (data.success) {
        setSubscribers(prev => prev.map(s => s.id === id ? { ...s, status: newStatus as 'active' | 'unsubscribed' } : s));
      } else alert(data.error || 'Greška');
    } catch { alert('Greška pri povezivanju'); }
  };

  const handleUpdateSubscriberGroups = async (subscriberId: number, groupIds: number[]) => {
    try {
      const res = await client.api.fetch(`/admin/newsletter/subscribers/${subscriberId}/groups`, {
        method: 'PUT', body: JSON.stringify({ groupIds }),
      });
      const data = await res.json();
      if (data.success) {
        setSubscribers(prev => prev.map(s => s.id === subscriberId ? { ...s, group_ids: groupIds } : s));
        setShowAssignGroupModal(null);
        loadData(); // Refresh group counts
      } else alert(data.error || 'Greška');
    } catch { alert('Greška pri povezivanju'); }
  };

  // ---- Group actions ----

  const handleSaveGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!groupForm.name.trim()) { alert('Naziv je obavezan'); return; }
    try {
      const url = editingGroup
        ? `/admin/newsletter/groups/${editingGroup.id}`
        : '/admin/newsletter/groups';
      const method = editingGroup ? 'PUT' : 'POST';
      const res = await client.api.fetch(url, {
        method, body: JSON.stringify(groupForm),
      });
      const data = await res.json();
      if (data.success) {
        setShowGroupModal(false);
        setGroupForm({ name: '', description: '', color: '#f97316' });
        setEditingGroup(null);
        loadData();
      } else alert(data.error || 'Greška');
    } catch { alert('Greška pri povezivanju'); }
  };

  const handleDeleteGroup = async (id: number) => {
    if (!window.confirm('Obrisati ovu grupu? Pretplatnici neće biti obrisani.')) return;
    try {
      const res = await client.api.fetch(`/admin/newsletter/groups/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setGroups(prev => prev.filter(g => g.id !== id));
        setSubscribers(prev => prev.map(s => ({
          ...s, group_ids: s.group_ids.filter(gid => gid !== id)
        })));
      } else alert(data.error || 'Greška');
    } catch { alert('Greška pri povezivanju'); }
  };

  // ---- Send ----

  const handleSendNewsletter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sendForm.subject.trim() || !sendForm.message.trim()) {
      alert('Popunite sva polja'); return;
    }

    let targetCount = 0;
    const body: Record<string, unknown> = { subject: sendForm.subject, message: sendForm.message };

    if (sendTargetMode === 'selected' && selectedSubscribers.length > 0) {
      body.subscriberIds = selectedSubscribers;
      targetCount = selectedSubscribers.length;
    } else if (sendTargetMode === 'groups' && sendTargetGroupIds.length > 0) {
      body.groupIds = sendTargetGroupIds;
      const subsInGroups = new Set<number>();
      subscribers.forEach(s => {
        if (s.status === 'active' && s.group_ids.some(gid => sendTargetGroupIds.includes(gid))) {
          subsInGroups.add(s.id);
        }
      });
      targetCount = subsInGroups.size;
    } else {
      targetCount = subscribers.filter(s => s.status === 'active').length;
    }

    if (targetCount === 0) { alert('Nema pretplatnika za slanje'); return; }
    if (!window.confirm(`Poslati newsletter na ~${targetCount} email adresa?`)) return;

    setSending(true);
    try {
      const res = await client.api.fetch('/admin/newsletter/send', {
        method: 'POST', body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        alert(`Uspešno poslato na ${data.sentCount} adresa!`);
        setShowSendModal(false);
        setSendForm({ subject: '', message: '' });
        setSendTargetMode('all');
        setSendTargetGroupIds([]);
        setSelectedSubscribers([]);
      } else alert(data.error || 'Greška pri slanju');
    } catch { alert('Greška pri povezivanju'); }
    finally { setSending(false); }
  };

  // ---- Import ----

  const parseEmails = (text: string): Array<{ email: string; name: string }> => {
    const lines = text.split(/[\r\n]+/).map(l => l.trim()).filter(Boolean);
    return lines.map(line => {
      const angleBracket = line.match(/^(.+?)\s*<(.+?)>$/);
      if (angleBracket) return { name: angleBracket[1].trim(), email: angleBracket[2].trim() };

      const parts = line.split(/[,;\t]+/).map(p => p.trim());
      if (parts.length >= 2) {
        if (parts[0].includes('@')) return { email: parts[0], name: parts[1] };
        if (parts[1].includes('@')) return { name: parts[0], email: parts[1] };
      }
      return { email: parts[0], name: '' };
    }).filter(e => e.email.includes('@'));
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      setImportText(prev => prev ? prev + '\n' + text : text);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleBulkImport = async () => {
    const parsed = parseEmails(importText);
    if (parsed.length === 0) { alert('Nema validnih email adresa'); return; }
    if (!window.confirm(`Uvesti ${parsed.length} email adresa?`)) return;

    setImporting(true);
    setImportResult(null);
    try {
      const res = await client.api.fetch('/admin/newsletter/import', {
        method: 'POST',
        body: JSON.stringify({ emails: parsed, groupIds: importGroupIds }),
      });
      const data = await res.json();
      if (data.success) {
        setImportResult(data.message);
        setImportText('');
        setImportGroupIds([]);
        loadData();
      } else alert(data.error || 'Greška pri uvozu');
    } catch { alert('Greška pri povezivanju'); }
    finally { setImporting(false); }
  };

  // ---- Filtering ----

  const filteredSubscribers = subscribers.filter(sub => {
    const matchesSearch = sub.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (sub.name?.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || sub.status === statusFilter;
    const matchesGroup = groupFilter === 'all' || sub.group_ids.includes(groupFilter as number);
    return matchesSearch && matchesStatus && matchesGroup;
  });

  const activeCount = subscribers.filter(s => s.status === 'active').length;
  const unsubscribedCount = subscribers.filter(s => s.status === 'unsubscribed').length;

  const toggleSelectAll = () => {
    const ids = filteredSubscribers.map(s => s.id);
    setSelectedSubscribers(prev => prev.length === ids.length ? [] : ids);
  };

  const toggleSelect = (id: number) => {
    setSelectedSubscribers(prev =>
      prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]
    );
  };

  const getGroupById = (id: number) => groups.find(g => g.id === id);

  const GROUP_COLORS = ['#f97316', '#3b82f6', '#22c55e', '#a855f7', '#ef4444', '#06b6d4', '#ec4899', '#eab308'];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm">
          <div className="text-2xl font-bold text-slate-900 dark:text-white">{subscribers.length}</div>
          <div className="text-sm text-slate-500 dark:text-slate-400">Ukupno</div>
        </div>
        <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm">
          <div className="text-2xl font-bold text-green-600">{activeCount}</div>
          <div className="text-sm text-slate-500 dark:text-slate-400">Aktivnih</div>
        </div>
        <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm">
          <div className="text-2xl font-bold text-red-500">{unsubscribedCount}</div>
          <div className="text-sm text-slate-500 dark:text-slate-400">Odjavljenih</div>
        </div>
        <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm">
          <div className="text-2xl font-bold text-purple-600">{groups.length}</div>
          <div className="text-sm text-slate-500 dark:text-slate-400">Grupa</div>
        </div>
      </div>

      {/* Panel Tabs */}
      <div className="flex gap-2 flex-wrap">
        {(['subscribers', 'groups', 'import'] as ActivePanel[]).map(panel => (
          <button key={panel} onClick={() => setActivePanel(panel)}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              activePanel === panel
                ? 'bg-amber-500 text-slate-900'
                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
            }`}>
            {panel === 'subscribers' ? 'Pretplatnici' : panel === 'groups' ? 'Grupe' : 'Uvoz email-ova'}
          </button>
        ))}
      </div>

      {/* ==================== SUBSCRIBERS PANEL ==================== */}
      {activePanel === 'subscribers' && (
        <>
          {/* Filters & Actions */}
          <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm">
            <div className="flex flex-col md:flex-row gap-3 items-center justify-between">
              <div className="flex gap-3 w-full md:w-auto flex-wrap">
                <input type="text" placeholder="Pretraži..." value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="flex-1 md:w-48 px-3 py-2 bg-slate-100 dark:bg-slate-700 rounded-lg text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as 'all' | 'active' | 'unsubscribed')}
                  className="px-3 py-2 bg-slate-100 dark:bg-slate-700 rounded-lg text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500">
                  <option value="all">Svi statusi</option>
                  <option value="active">Aktivni</option>
                  <option value="unsubscribed">Odjavljeni</option>
                </select>
                <select value={groupFilter} onChange={e => setGroupFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                  className="px-3 py-2 bg-slate-100 dark:bg-slate-700 rounded-lg text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500">
                  <option value="all">Sve grupe</option>
                  {groups.map(g => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>
              <button onClick={() => setShowSendModal(true)}
                className="w-full md:w-auto px-5 py-2 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400 transition-colors flex items-center justify-center gap-2 text-sm">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Pošalji Newsletter
              </button>
            </div>
            {selectedSubscribers.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700 flex items-center gap-3">
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  Selektovano: <strong>{selectedSubscribers.length}</strong>
                </span>
                <button onClick={() => setSelectedSubscribers([])}
                  className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 underline">
                  Poništi selekciju
                </button>
              </div>
            )}
          </div>

          {/* Subscribers Table */}
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-700">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input type="checkbox"
                        checked={selectedSubscribers.length === filteredSubscribers.length && filteredSubscribers.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded border-slate-300 dark:border-slate-600" />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Ime</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Grupe</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Izvor</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Datum</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Akcije</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {filteredSubscribers.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">Nema pretplatnika</td></tr>
                  ) : (
                    filteredSubscribers.map(sub => (
                      <tr key={sub.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                        <td className="px-4 py-3">
                          <input type="checkbox" checked={selectedSubscribers.includes(sub.id)}
                            onChange={() => toggleSelect(sub.id)}
                            className="w-4 h-4 rounded border-slate-300 dark:border-slate-600" />
                        </td>
                        <td className="px-4 py-3 text-slate-900 dark:text-white text-sm">
                          {sub.email}
                          {sub.user_id && (
                            <span className="ml-1.5 text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-1.5 py-0.5 rounded">
                              Korisnik
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400 text-sm">{sub.name || '-'}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 flex-wrap items-center">
                            {sub.group_ids.length > 0 ? sub.group_ids.map(gid => {
                              const group = getGroupById(gid);
                              return group ? (
                                <span key={gid} className="text-xs px-1.5 py-0.5 rounded text-white"
                                  style={{ backgroundColor: group.color }}>
                                  {group.name}
                                </span>
                              ) : null;
                            }) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                            <button onClick={() => setShowAssignGroupModal(sub.id)}
                              className="text-xs text-amber-600 hover:text-amber-500 ml-1" title="Izmeni grupe">
                              ✎
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            sub.status === 'active'
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                          }`}>
                            {sub.status === 'active' ? 'Aktivan' : 'Odjavljen'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            sub.source === 'registration'
                              ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                              : sub.source === 'homepage_form'
                              ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                              : sub.source === 'import'
                              ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400'
                              : 'bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-400'
                          }`}>
                            {sub.source === 'registration' ? 'Registracija'
                              : sub.source === 'homepage_form' ? 'Početna'
                              : sub.source === 'admin' ? 'Admin'
                              : sub.source === 'import' ? 'Import'
                              : 'Nepoznato'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">
                          {new Date(sub.subscribed_at).toLocaleDateString('sr-RS')}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button onClick={() => handleToggleStatus(sub.id, sub.status)}
                              className={`text-xs font-medium ${
                                sub.status === 'active' ? 'text-yellow-600 hover:text-yellow-500' : 'text-green-600 hover:text-green-500'
                              }`}>
                              {sub.status === 'active' ? 'Deaktiviraj' : 'Aktiviraj'}
                            </button>
                            <button onClick={() => handleDeleteSubscriber(sub.id)}
                              className="text-red-600 hover:text-red-500 text-xs font-medium">Obriši</button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ==================== GROUPS PANEL ==================== */}
      {activePanel === 'groups' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Grupe pretplatnika</h3>
            <button onClick={() => { setEditingGroup(null); setGroupForm({ name: '', description: '', color: '#f97316' }); setShowGroupModal(true); }}
              className="px-4 py-2 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400 transition-colors text-sm">
              + Nova grupa
            </button>
          </div>

          {groups.length === 0 ? (
            <div className="bg-white dark:bg-slate-800 p-8 rounded-lg shadow-sm text-center">
              <p className="text-slate-500 dark:text-slate-400">Nema kreiranih grupa.</p>
              <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">Kreirajte grupe da biste organizovali pretplatnike (npr. "Agencije", "Investitori", "Kupci").</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {groups.map(group => (
                <div key={group.id} className="bg-white dark:bg-slate-800 rounded-lg shadow-sm overflow-hidden">
                  <div className="h-2" style={{ backgroundColor: group.color }} />
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-slate-900 dark:text-white">{group.name}</h4>
                        {group.description && (
                          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{group.description}</p>
                        )}
                      </div>
                      <span className="text-2xl font-bold text-slate-300 dark:text-slate-600">{group.subscriber_count}</span>
                    </div>
                    <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                      <button onClick={() => {
                        setEditingGroup(group);
                        setGroupForm({ name: group.name, description: group.description || '', color: group.color });
                        setShowGroupModal(true);
                      }} className="text-xs text-amber-600 hover:text-amber-500 font-medium">Izmeni</button>
                      <button onClick={() => handleDeleteGroup(group.id)}
                        className="text-xs text-red-600 hover:text-red-500 font-medium">Obriši</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ==================== IMPORT PANEL ==================== */}
      {activePanel === 'import' && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-6 space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Masovni uvoz email adresa</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Unesite email adrese ili uploadujte .txt/.csv fajl. Podržani formati po liniji:
            </p>
            <div className="mt-2 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg text-xs text-slate-600 dark:text-slate-400 font-mono space-y-1">
              <div>email@primer.com</div>
              <div>email@primer.com, Ime Prezime</div>
              <div>Ime Prezime, email@primer.com</div>
              <div>Ime Prezime &lt;email@primer.com&gt;</div>
            </div>
          </div>

          {/* File upload */}
          <div>
            <input type="file" ref={fileInputRef} accept=".txt,.csv" onChange={handleFileUpload} className="hidden" />
            <button onClick={() => fileInputRef.current?.click()}
              className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors text-sm font-medium flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Uploaduj fajl (.txt, .csv)
            </button>
          </div>

          {/* Text area */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Email adrese (jedna po liniji)
            </label>
            <textarea value={importText} onChange={e => setImportText(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-700 rounded-lg text-slate-900 dark:text-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-500 min-h-[200px]"
              placeholder={`email1@primer.com\nemail2@primer.com, Ime Prezime\nIme Prezime <email3@primer.com>`} />
            {importText && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Prepoznato adresa: <strong>{parseEmails(importText).length}</strong>
              </p>
            )}
          </div>

          {/* Assign to groups */}
          {groups.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Dodaj u grupe (opciono)
              </label>
              <div className="flex gap-2 flex-wrap">
                {groups.map(g => (
                  <button key={g.id}
                    onClick={() => setImportGroupIds(prev =>
                      prev.includes(g.id) ? prev.filter(id => id !== g.id) : [...prev, g.id]
                    )}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                      importGroupIds.includes(g.id)
                        ? 'text-white border-transparent'
                        : 'text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-600'
                    }`}
                    style={importGroupIds.includes(g.id) ? { backgroundColor: g.color } : {}}>
                    {g.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Import button */}
          <div className="flex items-center gap-4">
            <button onClick={handleBulkImport} disabled={importing || !importText.trim()}
              className="px-6 py-2 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400 transition-colors disabled:opacity-50 text-sm">
              {importing ? 'Uvozi se...' : 'Uvezi email adrese'}
            </button>
            {importResult && (
              <span className="text-sm text-green-600 dark:text-green-400">{importResult}</span>
            )}
          </div>
        </div>
      )}

      {/* ==================== SEND NEWSLETTER MODAL ==================== */}
      {showSendModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Pošalji Newsletter</h2>

              {/* Target selection */}
              <div className="mb-4 space-y-3">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Pošalji na:</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="target" checked={sendTargetMode === 'all'}
                      onChange={() => setSendTargetMode('all')}
                      className="w-4 h-4 text-amber-500" />
                    <span className="text-sm text-slate-700 dark:text-slate-300">
                      Sve aktivne pretplatnike ({activeCount})
                    </span>
                  </label>
                  {selectedSubscribers.length > 0 && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="target" checked={sendTargetMode === 'selected'}
                        onChange={() => setSendTargetMode('selected')}
                        className="w-4 h-4 text-amber-500" />
                      <span className="text-sm text-slate-700 dark:text-slate-300">
                        Selektovane pretplatnike ({selectedSubscribers.length})
                      </span>
                    </label>
                  )}
                  {groups.length > 0 && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="target" checked={sendTargetMode === 'groups'}
                        onChange={() => setSendTargetMode('groups')}
                        className="w-4 h-4 text-amber-500" />
                      <span className="text-sm text-slate-700 dark:text-slate-300">
                        Odabrane grupe
                      </span>
                    </label>
                  )}
                </div>

                {/* Group checkboxes */}
                {sendTargetMode === 'groups' && groups.length > 0 && (
                  <div className="ml-6 flex gap-2 flex-wrap">
                    {groups.map(g => (
                      <button key={g.id}
                        onClick={() => setSendTargetGroupIds(prev =>
                          prev.includes(g.id) ? prev.filter(id => id !== g.id) : [...prev, g.id]
                        )}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                          sendTargetGroupIds.includes(g.id)
                            ? 'text-white border-transparent'
                            : 'text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-600'
                        }`}
                        style={sendTargetGroupIds.includes(g.id) ? { backgroundColor: g.color } : {}}>
                        {g.name} ({g.subscriber_count})
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <form onSubmit={handleSendNewsletter} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Naslov</label>
                  <input type="text" value={sendForm.subject}
                    onChange={e => setSendForm({ ...sendForm, subject: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-100 dark:bg-slate-700 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="Naslov email-a..." required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Poruka
                    <span className="ml-2 text-xs text-slate-400 font-normal">Koristite {'{name}'} i {'{email}'} za personalizaciju</span>
                  </label>
                  <textarea value={sendForm.message}
                    onChange={e => setSendForm({ ...sendForm, message: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-100 dark:bg-slate-700 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 min-h-[200px]"
                    placeholder="Sadržaj newsletter-a..." required />
                </div>
                <div className="flex gap-4 justify-end">
                  <button type="button"
                    onClick={() => { setShowSendModal(false); setSendTargetMode('all'); setSendTargetGroupIds([]); }}
                    className="px-6 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white">
                    Otkaži
                  </button>
                  <button type="submit" disabled={sending || (sendTargetMode === 'groups' && sendTargetGroupIds.length === 0)}
                    className="px-6 py-2 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400 transition-colors disabled:opacity-50">
                    {sending ? 'Šalje se...' : 'Pošalji'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ==================== GROUP CREATE/EDIT MODAL ==================== */}
      {showGroupModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">
                {editingGroup ? 'Izmeni grupu' : 'Nova grupa'}
              </h2>
              <form onSubmit={handleSaveGroup} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Naziv</label>
                  <input type="text" value={groupForm.name}
                    onChange={e => setGroupForm({ ...groupForm, name: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-100 dark:bg-slate-700 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="npr. Agencije, Investitori..." required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Opis (opciono)</label>
                  <input type="text" value={groupForm.description}
                    onChange={e => setGroupForm({ ...groupForm, description: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-100 dark:bg-slate-700 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="Kratak opis grupe..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Boja</label>
                  <div className="flex gap-2 flex-wrap">
                    {GROUP_COLORS.map(c => (
                      <button type="button" key={c} onClick={() => setGroupForm({ ...groupForm, color: c })}
                        className={`w-8 h-8 rounded-full border-2 transition-transform ${
                          groupForm.color === c ? 'border-slate-900 dark:border-white scale-110' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: c }} />
                    ))}
                  </div>
                </div>
                <div className="flex gap-4 justify-end pt-2">
                  <button type="button" onClick={() => { setShowGroupModal(false); setEditingGroup(null); }}
                    className="px-6 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white">
                    Otkaži
                  </button>
                  <button type="submit"
                    className="px-6 py-2 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400 transition-colors">
                    {editingGroup ? 'Sačuvaj' : 'Kreiraj'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ==================== ASSIGN GROUP MODAL ==================== */}
      {showAssignGroupModal !== null && (() => {
        const sub = subscribers.find(s => s.id === showAssignGroupModal);
        if (!sub) return null;
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-xl max-w-md w-full">
              <div className="p-6">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-1">Grupe za pretplatnika</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{sub.email}</p>
                {groups.length === 0 ? (
                  <p className="text-sm text-slate-500 dark:text-slate-400 py-4">Nema kreiranih grupa. Najpre kreirajte grupu na tabu "Grupe".</p>
                ) : (
                  <div className="space-y-2 mb-4">
                    {groups.map(g => {
                      const isIn = sub.group_ids.includes(g.id);
                      return (
                        <label key={g.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer">
                          <input type="checkbox" checked={isIn}
                            onChange={() => {
                              const newGroupIds = isIn
                                ? sub.group_ids.filter(gid => gid !== g.id)
                                : [...sub.group_ids, g.id];
                              setSubscribers(prev => prev.map(s =>
                                s.id === sub.id ? { ...s, group_ids: newGroupIds } : s
                              ));
                            }}
                            className="w-4 h-4 rounded" />
                          <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: g.color }} />
                          <span className="text-sm text-slate-700 dark:text-slate-300">{g.name}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                <div className="flex gap-4 justify-end pt-2 border-t border-slate-200 dark:border-slate-700">
                  <button onClick={() => setShowAssignGroupModal(null)}
                    className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white text-sm">
                    Otkaži
                  </button>
                  <button onClick={() => handleUpdateSubscriberGroups(sub.id, sub.group_ids)}
                    className="px-4 py-2 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400 transition-colors text-sm">
                    Sačuvaj
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
